import type { Participant } from '@wtfalch/calls/browser';
import { Button, Icon } from '@wtfalch/design';
import type { ReactNode } from 'react';

import { guestDisplayName } from './guestName.ts';

/**
 * The call's side panel: who is in the call, and a nudge to put the follow-up
 * in the calendar. `onBook` opens a new event; the panel does not pick a time
 * because nothing in the call knows who is free.
 */
export function CallSidePanel({
  participants,
  onBook,
  onClose,
  children,
}: {
  participants: Participant[];
  onBook?: () => void;
  onClose: () => void;
  /** Member-only sections, such as the lobby. */
  children?: ReactNode;
}) {
  return (
    <aside
      aria-label="The call"
      className="flex flex-col flex-none gap-4 w-80 max-w-full min-h-0 overflow-y-auto p-4 border-l border-border"
    >
      {children}
      <div className="flex items-center gap-2">
        <h3 className="h3 flex-1">In this call</h3>
        <Button size="sm" kind="ghost" iconOnly aria-label="Hide the call panel" onPress={onClose}>
          <Icon name="close" size={16} />
        </Button>
      </div>
      <ul className="flex flex-col gap-2 m-0 p-0 list-none">
        {participants.map((person) => (
          <li key={person.participantId} className="text-sm">
            {guestDisplayName(person.displayName, 'Someone')}
            {person.self ? ' (you)' : ''}
          </li>
        ))}
      </ul>
      {onBook && (
        <section className="flex flex-col gap-2 p-3 rounded-md border border-border">
          <h3 className="h3">Book a follow-up</h3>
          <p className="m-0 text-sm text-muted">
            Agreed to meet again? Put it in the calendar now.
          </p>
          <Button kind="primary" size="sm" onPress={onBook}>
            <Icon name="calendar" size={16} />
            <span>Book it</span>
          </Button>
        </section>
      )}
    </aside>
  );
}
